import { Component, ViewChild, Input, ChangeDetectionStrategy, Output, EventEmitter, OnChanges, SimpleChanges, AfterViewInit, ChangeDetectorRef, OnInit, HostBinding } from '@angular/core';
import { PopperHelper, PropertyViewModelInterface } from '@nts/std';
import { Editor } from 'primeng/editor';
import { merge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NgxPopperjsPlacements, NgxPopperjsTriggers, NgxPopperjsDirective, NgxPopperjsModule } from "ngx-popperjs";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Meta } from '@angular/platform-browser';
import { MobileHelper } from '@nts/std/src/lib/utility';
import Mention from 'quill-mention';
import Quill from 'quill';
import { NgFor, NgIf } from '@angular/common';
import { EditorModule } from 'primeng/editor';
import { FormsModule } from '@angular/forms';

@UntilDestroy()
@Component({
    selector: 'nts-custom-editor',
    templateUrl: './custom-editor.component.html',
    styleUrls: ['./custom-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        NgxPopperjsModule,
        NgFor,
        NgIf,
        EditorModule,
        FormsModule
    ]

})
export class CustomEditorComponent implements OnChanges, AfterViewInit, OnInit {

    @Input() propertyViewModel: PropertyViewModelInterface;
    @Input() placeHolder: string = "";
    @Input() enableFullscreen: boolean = false;
    @Input() showToolbar = false;
    @Input() variables: { id: number, value: string }[] = [];

    @HostBinding('class.full-column')
    @Input() fullColumn = false;

    @HostBinding('class.multiline')
    @Input() multiline = false;

    @Output() toggleFullscreen = new EventEmitter();

    @ViewChild('message') editor: Editor;
    @ViewChild('popperError', { static: false }) popperError: NgxPopperjsDirective;

    ngxPopperjsTriggers = NgxPopperjsTriggers;
    ngxPopperjsPlacements = NgxPopperjsPlacements;
    modules = {};

    protected destroyOnChange$: Subject<boolean> = new Subject<boolean>();

    private internalFullscreen = false

    get fullscreen() {
        return this.internalFullscreen;
    }

    constructor(
        private meta: Meta,
        private readonly cd: ChangeDetectorRef
    ) {

    }

    ngOnInit(): void {

    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['propertyViewModel']) {
            this.destroyOnChange$.next(true);
            this.updateToolbarEnabledStatus();
            merge(
                this.propertyViewModel.customGetterValueChanged,
                this.propertyViewModel.propertyViewModelChanged,
                this.propertyViewModel.onErrorStatusChanged,
            ).pipe(
                untilDestroyed(this),
                takeUntil(this.destroyOnChange$)
            ).subscribe((x) => {
                this.cd.detectChanges();
            });

            // Rimane in ascolto quando cambiano gli attributi del pvm
            this.propertyViewModel.propertyChanged.pipe(
                untilDestroyed(this),
                takeUntil(this.destroyOnChange$)
            ).subscribe((arg) => {
                if (arg.propertyName === 'isEnabled') {
                    this.updateToolbarEnabledStatus();
                }
                this.cd.detectChanges();
            });

            this.propertyViewModel.onFocusRequested.pipe(
                untilDestroyed(this),
                takeUntil(this.destroyOnChange$)
            ).subscribe(() => {
                this.focus(this.propertyViewModel.hasErrors);
                // this.editor.quill.setSelection(0, 5);
            });
        }
    }

    focus(showErroTooltip = false) {
        this.editor.quill.focus();
        if (showErroTooltip) {
            this.showErroTooltip();
        }
    }

    showErroTooltip(): void {
        PopperHelper.show(this.popperError);
    }

    ngAfterViewInit() {

        this.toggleFullscreen.pipe(untilDestroyed(this)).subscribe(() => this.internalFullscreen = !this.internalFullscreen);
        this.updateToolbarEnabledStatus();

        // imposto il tab index in ogni select della toolbar
        const selectFontSizeButton = this.editor.el.nativeElement.querySelector('.ql-size.ql-picker .ql-picker-label');
        selectFontSizeButton?.setAttribute('tabindex', -1);

        const selectHeaderButton = this.editor.el.nativeElement.querySelector('.ql-header.ql-picker .ql-picker-label');
        selectHeaderButton?.setAttribute('tabindex', -1);

        const selectFontColorButton = this.editor.el.nativeElement.querySelector('.ql-color.ql-picker.ql-color-picker .ql-picker-label');
        selectFontColorButton?.setAttribute('tabindex', -1);

        const selectFontBackgroundColorButton = this.editor.el.nativeElement.querySelector('.ql-background.ql-picker.ql-color-picker .ql-picker-label');
        selectFontBackgroundColorButton?.setAttribute('tabindex', -1);

        const selectFontFamilyButton = this.editor.el.nativeElement.querySelector('.ql-font.ql-picker .ql-picker-label');
        selectFontFamilyButton?.setAttribute('tabindex', -1);

        const selectTextAlignButton = this.editor.el.nativeElement.querySelector('.ql-align.ql-picker.ql-icon-picker .ql-picker-label');
        selectTextAlignButton?.setAttribute('tabindex', -1);

        MobileHelper.fixZoomIssue(this.editor.el.nativeElement, this.meta);

        const localQuill = this.editor.getQuill()

        const mentionOptions = {
            allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
            mentionDenotationChars: ["{"],
            showDenotationChar: false,
            source: (searchTerm, renderList, mentionChar) => {
                const values = this.variables || [];

                if (searchTerm.length === 0) {
                    renderList(values, searchTerm);
                } else {
                    const matches = [];
                    for (let i = 0; i < values.length; i++) {
                        if (
                            // https://wsvincent.com/javascript-tilde/
                            ~values[i].value.toLowerCase().indexOf(searchTerm.toLowerCase())
                        ) {
                            matches.push(values[i]);
                        }
                    }
                    renderList(matches, searchTerm);
                }
            }
        }

        this.modules = {
            mention: {
                ...mentionOptions
            },
            keyboard: {
                bindings: {
                    enter: {
                        key: 13,
                        handler: function () {
                            return false;
                        }
                    }
                }
            }
        };

        const mention = new Mention(localQuill, this.modules['mention'])
        Quill.register('modules/mention', mention);

        const SizeStyle = Quill.import('attributors/style/size');
        SizeStyle.whitelist.push('13px');
        SizeStyle.whitelist.push('0.75em');
        SizeStyle.whitelist.push('1.5em');
        SizeStyle.whitelist.push('2.5em');
        Quill.register(SizeStyle, true);

        const font = Quill.import('attributors/style/font');
        font.whitelist.push('sans-serif');
        font.whitelist.push('serif');
        font.whitelist.push('monospace');
        Quill.register(font, true);

        // Se non sono multiline devo disabilitare l'invio
        if (!this.multiline) {
            const keyboard = localQuill.getModule('keyboard');
            keyboard.bindings[13] = [{
                key: 13,
                // Faccio ritornare sempre false (così non vado a capo) chiamando la funzione di mention per gestire la selezione delle variabili con invio
                handler: () => mention.selectHandler.call(mention) && false
            }];
        } else {
            // vedi https://github.com/quilljs/quill/issues/252#issuecomment-288884372
            const keyboard = localQuill.getModule('keyboard');
            const clipboard = localQuill.getModule('clipboard');
            const Break = Quill.import('blots/break');
            const Delta = Quill.import('delta');
            const Embed = Quill.import('blots/embed');
            const lineBreakMatcher = (node, delta) => {
                var newDelta = new Delta();
                newDelta.insert({ 'break': '' });
                return newDelta;
            }
            Break.prototype.insertInto = function (parent, ref) {
                Embed.prototype.insertInto.call(this, parent, ref)
            };
            Break.prototype.length = function () {
                return 1;
            }
            Break.prototype.value = function () {
                return '\n';
            }
            clipboard.addMatcher('br', lineBreakMatcher);

            keyboard.bindings[13] = [{
                key: 13,
                // Faccio ritornare sempre false (così non vado a capo) chiamando la funzione di mention per gestire la selezione delle variabili con invio
                handler: (range, context) => {
                    if (mention.selectHandler.call(mention)) {
                        const nextChar = localQuill.getText(range.index + 1, 1);
                        var ee = localQuill.insertEmbed(range.index, 'break', true, 'user');
                        if (nextChar.length == 0) {
                            // second line break inserts only at the end of parent element
                            var ee = localQuill.insertEmbed(range.index, 'break', true, 'user');
                        }
                        localQuill.setSelection(range.index + 1, Quill.sources.SILENT);
                    }
                }
            }];
        }
    }

    /*updateToolbarVisibility() {
        if (this.propertyViewModel.isEnabled) {
            this.editor.el.nativeElement.querySelector('.p-editor-toolbar').classList.remove("hide-top-toolbar");

        } else {
            this.editor.el.nativeElement.querySelector('.p-editor-toolbar').classList.add("hide-top-toolbar");
        }
    }*/

    updateToolbarEnabledStatus() {
        if (this.editor != undefined) {
            if (this.propertyViewModel.isEnabled) {
                // cambio classe per cambiare colore quando toolbar disabilitata
                this.editor.el.nativeElement.querySelector('.p-editor-toolbar').classList.remove("disable-top-toolbar");
                this.editor.el.nativeElement.querySelectorAll('.p-editor-toolbar > span > span[disabled]')
                    .forEach(element => {
                        element.removeAttribute('disabled');
                    });
            } else {
                this.editor.el.nativeElement.querySelector('.p-editor-toolbar').classList.add("disable-top-toolbar");
                this.editor.el.nativeElement.querySelectorAll('.p-editor-toolbar > span > span')
                    .forEach(element => {
                        element.setAttribute('disabled', '');
                    })
            }
        }

    }
}
